<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-dashboard-text flex items-center gap-2">
          <Users class="w-6 h-6" />
          Gestion des Utilisateurs
        </h2>
        <div class="flex gap-2">
          <button
            @click="openCreateModal"
            :disabled="!canCreateUser"
            :title="canCreateUser ? '' : 'Action non autorisée pour votre profil'"
            class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus class="w-4 h-4" />
            Nouvel utilisateur
          </button>
          <button
            @click="refreshUsers"
            class="px-4 py-2 text-[#22282F] bg-white rounded-lg hover:bg-gray-100 border border-[#22282F] transition-colors flex items-center gap-2"
          >
            <RefreshCw :class="{'animate-spin': loading}" class="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

    <!-- Search Bar -->
    <div class="mb-6 bg-white border border-dashboard-border rounded-xl p-4 shadow-sm">
      <div class="relative">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dashboard-muted" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Rechercher un utilisateur par nom, email ou rôle..."
          class="w-full pl-12 pr-4 py-3 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20 transition-all"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12 bg-white border border-dashboard-border rounded-xl">
      <div class="w-8 h-8 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin" />
    </div>

    <!-- Users Table -->
    <div v-else-if="filteredUsers.length" class="bg-white border border-dashboard-border rounded-xl shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-dashboard-bg">
            <tr>
              <th class="text-left py-4 px-6 text-sm font-semibold text-dashboard-text">Utilisateur</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-dashboard-text">Rôle</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-dashboard-text">Direction</th>
              <th class="text-center py-4 px-6 text-sm font-semibold text-dashboard-text">Statut</th>
              <th class="text-center py-4 px-6 text-sm font-semibold text-dashboard-text">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="user in paginatedUsers" 
              :key="user.id"
              class="border-t border-dashboard-border hover:bg-dashboard-bg transition-colors"
            >
              <!-- Utilisateur -->
              <td class="py-4 px-6">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-[#22282F]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User class="w-5 h-5 text-[#22282F]" />
                  </div>
                  <div>
                    <p class="text-sm font-medium text-dashboard-text">{{ user.full_name }}</p>
                    <p class="text-xs text-dashboard-muted">{{ user.email }}</p>
                  </div>
                </div>
              </td>

              <!-- Rôle -->
              <td class="py-4 px-6">
                <span 
                  v-if="user.role"
                  class="inline-flex items-center bg-gray-100 gap-1 px-2 py-1 rounded-full text-xs font-medium"
                >
                  <Shield class="w-3 h-3" />
                  {{ user.role.nom }}
                </span>
                <span v-else class="text-xs text-dashboard-muted italic">Aucun rôle</span>
              </td>

              <!-- Direction -->
              <td class="py-4 px-6">
                <span v-if="user.direction" class="text-sm text-dashboard-text">
                  {{ user.direction.code }}
                </span>
                <span v-else-if="user.ambassade" class="text-sm text-dashboard-text">
                  {{ user.ambassade.code }}
                </span>
                <span v-else class="text-xs text-dashboard-muted italic">-</span>
              </td>

              <!-- Statut -->
              <td class="py-4 px-6 text-center">
                <span 
                  :class="user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                >
                  <div :class="user.is_active ? 'bg-green-500' : 'bg-red-500'" class="w-1.5 h-1.5 rounded-full" />
                  {{ user.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </td>

              <!-- Actions -->
              <td class="py-4 px-6 text-center">
                <div class="flex items-center justify-center gap-2">
                  <button
                    @click="openEditModal(user.id)"
                    :disabled="!canEditUser"
                    :title="canEditUser ? 'Modifier' : 'Modification non autorisée pour votre profil'"
                    class="inline-flex items-center gap-1 px-3 py-1 text-[#22282F] border border-[#22282F]/10 rounded-lg text-xs font-medium hover:bg-[#22282F]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Edit3 class="w-3 h-3" />
                    Modifier
                  </button>
                  <button
                    @click="resendActivation(user)"
                    :disabled="resendingId === user.id || !canResendActivation || !user.email"
                    class="inline-flex items-center gap-1 px-3 py-1 text-[#22282F] border border-[#22282F]/10 rounded-lg text-xs font-medium hover:bg-[#22282F]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="!canResendActivation ? 'Action non autorisée pour votre profil' : (!user.email ? 'Ce compte n\'a pas d\'adresse email' : 'Renvoyer le mail de bienvenue (lien de définition du mot de passe)')"
                  >
                    <Mail :class="{'animate-pulse': resendingId === user.id}" class="w-3 h-3" />
                    Renvoyer l'email
                  </button>
                  <button
                    @click="toggleUserActive(user.id, user.is_active)"
                    :disabled="!canToggleActive"
                    :class="user.is_active ? 'bg-[#22282F] text-white border-[#22282F] hover:bg-orange-100' : 'bg-green-50 text-white border-green-200 hover:bg-green-100'"
                    class="hidden items-center gap-1 px-3 py-1 border rounded-lg text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="user.is_active ? 'Désactiver' : 'Activer'"
                  >
                    {{ user.is_active ? 'Désactiver' : 'Activer' }}
                  </button>
                  <button
                    @click="reset2FA(user)"
                    :disabled="resetting2FAId === user.id || !canReset2FA"
                    class="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="canReset2FA ? 'Réinitialiser la double authentification (2FA) de cet utilisateur' : 'Action non autorisée pour votre profil'"
                  >
                    <ShieldOff :class="{'animate-pulse': resetting2FAId === user.id}" class="w-3 h-3" />
                    {{ resetting2FAId === user.id ? 'En cours…' : 'Réinit. 2FA' }}
                  </button>
                  <button
                    @click="selectUser(user)"
                    class="inline-flex items-center gap-1 px-3 py-1 bg-dashboard-bg border border-dashboard-border rounded-lg text-xs font-medium text-dashboard-text hover:bg-[#22282F] hover:text-white hover:border-[#22282F] transition-all"
                    title="Voir détails"
                  >
                    <Eye class="w-3 h-3" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="border-t border-dashboard-border px-6 py-4 flex items-center justify-between">
        <p class="text-sm text-dashboard-muted">
          Page {{ currentPage }} sur {{ totalPages }} ({{ filteredUsers.length }} utilisateurs)
        </p>
        <div class="flex gap-2">
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="px-3 py-1 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm font-medium text-dashboard-text hover:bg-[#22282F] hover:text-white hover:border-[#22282F] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-dashboard-bg disabled:hover:text-dashboard-text disabled:hover:border-dashboard-border"
          >
            Précédent
          </button>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm font-medium text-dashboard-text hover:bg-[#22282F] hover:text-white hover:border-[#22282F] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-dashboard-bg disabled:hover:text-dashboard-text disabled:hover:border-dashboard-border"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white border border-dashboard-border rounded-xl p-12 text-center">
      <UserX class="w-16 h-16 text-dashboard-muted mx-auto mb-4" />
      <h3 class="text-lg font-semibold text-dashboard-text mb-2">Aucun utilisateur trouvé</h3>
      <p class="text-sm text-dashboard-muted">Essayez de modifier votre recherche</p>
    </div>

    <!-- User Detail Modal -->
    <div v-if="selectedUser" class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" @click.self="selectedUser = null">
      <div class="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-dashboard-border px-6 py-4 flex items-center justify-between">
          <h3 class="text-lg font-bold text-dashboard-text">Détails de l'utilisateur</h3>
          <button @click="selectedUser = null" class="p-2 hover:bg-dashboard-bg rounded-lg transition-colors">
            <X class="w-5 h-5" />
          </button>
        </div>
        <div class="p-6 space-y-4">
          <div class="flex items-center gap-4 pb-4 border-b border-dashboard-border">
            <div class="w-16 h-16 bg-[#22282F]/10 rounded-full flex items-center justify-center">
              <User class="w-8 h-8 text-[#22282F]" />
            </div>
            <div>
              <h4 class="text-xl font-bold text-dashboard-text">{{ selectedUser.full_name }}</h4>
              <p class="text-sm text-dashboard-muted">{{ selectedUser.email }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Username</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser.username }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Statut</label>
              <p class="text-sm">
                <span 
                  :class="selectedUser.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                >
                  {{ selectedUser.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Rôle</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser?.role?.nom || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Direction</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser?.direction?.nom || selectedUser?.ambassade?.nom || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Département</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser?.departement?.nom || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Service</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser?.service?.nom || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Matricule</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser.matricule || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Téléphone</label>
              <p class="text-sm text-dashboard-text">{{ selectedUser.telephone || '-' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-dashboard-muted">Double authentification</label>
              <p class="text-sm">
                <span
                  :class="selectedUser.two_factor_enabled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                >
                  {{ selectedUser.two_factor_enabled ? 'Activée' : 'Non configurée' }}
                </span>
              </p>
            </div>
          </div>

          <!-- Action reset 2FA -->
          <div v-if="selectedUser.two_factor_enabled" class="pt-4 border-t border-dashboard-border">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-dashboard-text">Réinitialiser la 2FA</p>
                <p class="text-xs text-dashboard-muted mt-0.5">L'utilisateur devra reconfigurer son application d'authentification à la prochaine connexion.</p>
              </div>
              <button
                @click="reset2FA(selectedUser)"
                :disabled="resetting2FAId === selectedUser.id"
                class="inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                <ShieldOff class="w-3.5 h-3.5" />
                {{ resetting2FAId === selectedUser.id ? 'En cours…' : 'Réinitialiser' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Form Modal -->
    <UserFormModal 
      :is-open="showModal" 
      :user-id="editingUserId"
      @close="closeModal"
      @success="handleModalSuccess"
    />
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Users, User, UserX, UserPlus, Shield, Search, RefreshCw, Edit3, Eye, X, Mail, ShieldOff } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import UserFormModal from '@/components/UserFormModal.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { useAuthStore } from '@/store/auth'

const toast = useToast()
const authStore = useAuthStore()

// Permissions d'écriture (profil restreint = boutons désactivés).
const canCreateUser = computed(() => authStore.canAction('users.create'))
const canEditUser = computed(() => authStore.canAction('users.edit'))
const canResendActivation = computed(() => authStore.canAction('users.resend_activation'))
const canToggleActive = computed(() => authStore.canAction('users.toggle_active'))
const canReset2FA = computed(() => authStore.canAction('users.reset_2fa'))

const users = ref<any[]>([])
const loading = ref(false)
const searchQuery = ref('')
const selectedUser = ref<any>(null)
const currentPage = ref(1)
const perPage = 10

// Modal state
const showModal = ref(false)
const editingUserId = ref<number | null>(null)

// Renvoi d'email d'activation en cours (id de l'utilisateur ciblé)
const resendingId = ref<number | null>(null)
const resetting2FAId = ref<number | null>(null)

const filteredUsers = computed(() => {
  if (!searchQuery.value) return users.value
  const query = searchQuery.value.toLowerCase()
  return users.value.filter(user => 
    user.full_name.toLowerCase().includes(query) ||
    user.email.toLowerCase().includes(query) ||
    user.username.toLowerCase().includes(query) ||
    user.role?.nom?.toLowerCase().includes(query)
  )
})

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * perPage
  const end = start + perPage
  return filteredUsers.value.slice(start, end)
})

const totalPages = computed(() => Math.ceil(filteredUsers.value.length / perPage))

const fetchUsers = async () => {
  loading.value = true
  try {
    users.value = await apiFetch('/api/admin/users/')
  } catch (error: any) {
    toast.error('Erreur de chargement', error.message || 'Impossible de charger les utilisateurs')
  } finally {
    loading.value = false
  }
}

const refreshUsers = () => {
  currentPage.value = 1
  fetchUsers()
}

const selectUser = (user: any) => {
  selectedUser.value = user
}

const openCreateModal = () => {
  editingUserId.value = null
  showModal.value = true
}

const openEditModal = (userId: number) => {
  editingUserId.value = userId
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingUserId.value = null
}

const handleModalSuccess = () => {
  fetchUsers()
}

const toggleUserActive = async (userId: number, currentStatus: boolean) => {
  try {
    await apiFetch(`/api/admin/users/${userId}/toggle-active/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
    
    toast.success(
      'Statut modifié',
      `L'utilisateur a été ${currentStatus ? 'désactivé' : 'activé'} avec succès`
    )
    fetchUsers()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de modifier le statut')
  }
}

const resendActivation = async (user: any) => {
  if (resendingId.value) return
  const ok = window.confirm(
    `Renvoyer le mail de bienvenue à ${user.full_name || user.username} (${user.email}) ?\n\n` +
    `Un lien d'activation lui permettra de définir son mot de passe.`
  )
  if (!ok) return

  resendingId.value = user.id
  try {
    // Endpoint d'administration : authentifié et soumis à la permission
    // `users.resend_activation`, contrôlée par le serveur.
    //
    // Auparavant ce bouton appelait /api/auth/request-reset/, un endpoint
    // PUBLIC : la permission ne faisait alors que griser le bouton, et
    // n'importe qui pouvait déclencher l'envoi sans même être connecté.
    // Cacher un bouton ne protège rien — la barrière doit être côté serveur.
    await apiFetch(`/api/admin/users/${user.id}/resend-activation/`, {
      method: 'POST',
    })
    toast.success('Email envoyé', `Un lien a été envoyé à ${user.email}`)
  } catch (error: any) {
    toast.error('Erreur', error.message || "Impossible d'envoyer l'email")
  } finally {
    resendingId.value = null
  }
}

const getRoleBadgeClass = (code: string) => {
  const classes: Record<string, string> = {
    'ADMIN': 'bg-red-50 text-red-700',
    'SG': 'bg-purple-50 text-purple-700',
    'SGA': 'bg-indigo-50 text-indigo-700',
    'DIRECTEUR': 'bg-blue-50 text-blue-700',
    'CHEF_DEPT': 'bg-cyan-50 text-cyan-700',
    'CHEF_SERVICE': 'bg-teal-50 text-teal-700',
    'AGENT': 'bg-gray-50 text-gray-700',
    'SECRETARIAT': 'bg-pink-50 text-pink-700',
  }
  return classes[code] || 'bg-gray-50 text-gray-700'
}

const reset2FA = async (user: any) => {
  const ok = window.confirm(
    `Réinitialiser la double authentification de ${user.full_name || user.username} ?\n\n` +
    `L'utilisateur devra reconfigurer son application d'authentification à sa prochaine connexion.`
  )
  if (!ok) return

  resetting2FAId.value = user.id
  try {
    await apiFetch(`/api/admin/users/${user.id}/reset-2fa/`, { method: 'POST' })
    toast.success('2FA réinitialisée', `La double authentification de ${user.full_name || user.username} a été réinitialisée.`)
    if (selectedUser.value?.id === user.id) {
      selectedUser.value = { ...selectedUser.value, two_factor_enabled: false }
    }
    fetchUsers()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de réinitialiser la 2FA')
  } finally {
    resetting2FAId.value = null
  }
}

onMounted(() => {
  fetchUsers()
})
</script>

<style scoped>
</style>
